import * as THREE from 'three';
import { C, M, mesh, piece, canvasTexture, roundRect, FONT } from './accessories.js';

/*
 * GoCall's own props for Kli: the voice agent. Built from the same parts as the
 * KaliAI ones (accessories.js) so they share the look.
 */

/* Phone on a live call: avatar, name, a running timer and a talking waveform.
   mode 'live' = in a call; 'ring' = incoming call, the phone shakes until poked. */
export function callPhone(kli, { x = 1.55, y = 0.05, z = 0.25, ry = -0.38, mode = 'live', name = 'דנה · GoCall' } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.rotation.set(-0.05, ry, 0.06);
  const shape = new THREE.Shape();
  {
    const w = 0.8, h = 1.56, r = 0.12, x0 = -0.4, y0 = -0.78;
    shape.moveTo(x0 + r, y0); shape.lineTo(x0 + w - r, y0); shape.quadraticCurveTo(x0 + w, y0, x0 + w, y0 + r);
    shape.lineTo(x0 + w, y0 + h - r); shape.quadraticCurveTo(x0 + w, y0 + h, x0 + w - r, y0 + h);
    shape.lineTo(x0 + r, y0 + h); shape.quadraticCurveTo(x0, y0 + h, x0, y0 + h - r);
    shape.lineTo(x0, y0 + r); shape.quadraticCurveTo(x0, y0, x0 + r, y0);
  }
  const body = new THREE.ExtrudeGeometry(shape, { depth: 0.06, bevelEnabled: true, bevelSize: 0.02, bevelThickness: 0.02, bevelSegments: 3, curveSegments: 10 });
  body.translate(0, 0, -0.03);
  g.add(mesh(body, M.ink));

  let state = mode;        // 'ring' | 'live'
  let seconds = 0, level = 0, talking = true;
  const bars = Array.from({ length: 13 }, () => 0.2);
  const screen = canvasTexture(360, 720, (c, w, h) => {
    const grad = c.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, '#16242E'); grad.addColorStop(1, '#0E1A22');
    c.fillStyle = grad; roundRect(c, 0, 0, w, h, 40); c.fill();
    c.direction = 'rtl'; c.textAlign = 'center';

    // avatar with a pulse ring
    const cx = w / 2, cy = 210;
    c.beginPath(); c.arc(cx, cy, 92 + level * 16, 0, Math.PI * 2);
    c.fillStyle = `rgba(45,172,223,${0.12 + level * 0.2})`; c.fill();
    c.beginPath(); c.arc(cx, cy, 74, 0, Math.PI * 2); c.fillStyle = C.blue; c.fill();
    c.fillStyle = '#fff'; c.font = `800 64px ${FONT}`; c.fillText('ד', cx, cy + 22);

    c.font = `700 30px ${FONT}`; c.fillText(name, cx, 350);
    c.font = `400 22px ${FONT}`; c.fillStyle = '#9FB4C1';
    const mm = String(Math.floor(seconds / 60)).padStart(2, '0'), ss = String(Math.floor(seconds % 60)).padStart(2, '0');
    c.fillText(state === 'ring' ? 'שיחה נכנסת…' : `${mm}:${ss}`, cx, 388);

    // waveform
    const bw = 12, gap = 9, total = bars.length * (bw + gap) - gap;
    bars.forEach((b, i) => {
      const bh = 10 + b * 90;
      c.fillStyle = state === 'ring' ? '#3A4C58' : C.blue300;
      roundRect(c, (w - total) / 2 + i * (bw + gap), 470 - bh / 2, bw, bh, 6); c.fill();
    });

    // buttons
    if (state === 'ring') {
      c.beginPath(); c.arc(w / 2 - 80, 620, 38, 0, Math.PI * 2); c.fillStyle = '#E5484D'; c.fill();
      c.beginPath(); c.arc(w / 2 + 80, 620, 38, 0, Math.PI * 2); c.fillStyle = '#2FB36B'; c.fill();
    } else {
      [-90, 0].forEach((dx) => { c.beginPath(); c.arc(w / 2 + dx, 620, 32, 0, Math.PI * 2); c.fillStyle = '#26363F'; c.fill(); });
      c.beginPath(); c.arc(w / 2 + 90, 620, 32, 0, Math.PI * 2); c.fillStyle = '#E5484D'; c.fill();
    }
  });
  const scr = mesh(new THREE.PlaneGeometry(0.72, 1.44), new THREE.MeshBasicMaterial({ map: screen.tex, toneMapped: false, transparent: true }), [0, 0, 0.056]);
  g.add(scr);
  kli.stage.add(g);
  document.fonts?.ready.then(() => screen.redraw());

  let redrawAt = 0;
  const api = piece([g], (dt, t) => {
    if (state === 'ring') {
      g.rotation.z = 0.06 + Math.sin(t * 38) * 0.06 * (Math.sin(t * 3) > 0 ? 1 : 0);
      g.position.y = y + Math.sin(t * 1.5) * 0.04;
    } else {
      g.rotation.z = 0.06 + Math.sin(t * 1.1) * 0.03;
      g.position.y = y + Math.sin(t * 1.5) * 0.05;
      seconds += dt;
    }
    // the waveform follows "speech": bursts while talking, near-flat in pauses
    const target = state === 'live' && talking ? 0.35 + 0.65 * Math.abs(Math.sin(t * 7.3) * Math.sin(t * 2.1)) : 0.05;
    level += (target - level) * Math.min(1, dt * 10);
    redrawAt -= dt;
    if (redrawAt <= 0) {
      redrawAt = 0.08;
      for (let i = 0; i < bars.length; i++) {
        const want = state === 'live' && talking ? level * (0.35 + 0.65 * Math.abs(Math.sin(t * (4 + i * 0.7) + i))) : 0.08;
        bars[i] += (want - bars[i]) * 0.6;
      }
      screen.redraw();
    }
  }, () => { if (state === 'ring') { state = 'live'; seconds = 0; } talking = true; });
  api.setTalking = (on) => { talking = on; };
  api.ring = () => { state = 'ring'; seconds = 0; };
  api.answer = () => { state = 'live'; seconds = 0; talking = true; };
  return api;
}

/* Soundwave arcs next to Kli's mouth: speaking indicator (replaces the typing bubble). */
export function voiceWaves(kli, { x = -1.25, y = 0.35, z = 0.4 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  const arcs = [0, 1, 2].map((i) => {
    const m = mesh(new THREE.TorusGeometry(0.18 + i * 0.16, 0.035, 8, 32, Math.PI * 0.7),
      new THREE.MeshBasicMaterial({ color: C.blue, transparent: true, toneMapped: false, depthWrite: false }),
      [0, 0, 0], [0, 0, Math.PI * 0.65]);
    g.add(m);
    return m;
  });
  kli.stage.add(g);
  let on = false;
  const api = piece([g], (dt, t) => {
    arcs.forEach((a, i) => {
      const p = (Math.sin(t * 6 - i * 0.9) + 1) / 2;
      a.material.opacity = on ? 0.25 + p * 0.65 : 0;
    });
  });
  api.setOn = (v) => { on = v; };
  return api;
}
